import { api } from '../../../utils/client';
import { queryClient } from '../../../queryClient';
import { queryKeys } from '../../../queryKeys';
import type {
  BlobListResponse,
  CategoryTypeFormValue,
  CategoryTypeRow,
  MarketplaceMeta,
  ProductCategoryFormValue,
  ProductCategoryRow,
  ProductFormValue,
  ProductRow,
} from './types';
import {
  categoryTypeToPayload,
  productCategoryToPayload,
  productToPayload,
} from './types';

function asList<T>(data: T[] | { results?: T[] }): T[] {
  return Array.isArray(data) ? data : data?.results ?? [];
}

function invalidateMarketplaceCatalog() {
  return queryClient.invalidateQueries({ queryKey: queryKeys.marketplace.all });
}

export async function fetchMarketplaceMeta() {
  return api<MarketplaceMeta>('/api/admin/marketplace/meta/');
}

export async function fetchCategoryTypes() {
  const data = await api<CategoryTypeRow[] | { results: CategoryTypeRow[] }>(
    '/api/admin/category-types/'
  );
  return asList(data);
}

export async function createCategoryType(value: CategoryTypeFormValue) {
  const row = await api<CategoryTypeRow>('/api/admin/category-types/', {
    method: 'POST',
    body: JSON.stringify(categoryTypeToPayload(value)),
  });
  void invalidateMarketplaceCatalog();
  return row;
}

export async function updateCategoryType(id: number, value: CategoryTypeFormValue) {
  const row = await api<CategoryTypeRow>(`/api/admin/category-types/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(categoryTypeToPayload(value)),
  });
  void invalidateMarketplaceCatalog();
  return row;
}

export async function fetchProductCategories(categoryType?: number | '') {
  const search = new URLSearchParams();
  if (categoryType) search.set('category_type', String(categoryType));
  const qs = search.toString();
  const data = await api<ProductCategoryRow[] | { results: ProductCategoryRow[] }>(
    `/api/admin/product-categories/${qs ? `?${qs}` : ''}`
  );
  return asList(data);
}

export async function createProductCategory(value: ProductCategoryFormValue) {
  const row = await api<ProductCategoryRow>('/api/admin/product-categories/', {
    method: 'POST',
    body: JSON.stringify(productCategoryToPayload(value)),
  });
  void invalidateMarketplaceCatalog();
  return row;
}

export async function updateProductCategory(id: number, value: ProductCategoryFormValue) {
  const row = await api<ProductCategoryRow>(`/api/admin/product-categories/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(productCategoryToPayload(value)),
  });
  void invalidateMarketplaceCatalog();
  return row;
}

export async function fetchProducts(params?: {
  category?: number | '';
  category_type?: number | '';
}) {
  const search = new URLSearchParams();
  if (params?.category) search.set('category', String(params.category));
  if (params?.category_type) search.set('category_type', String(params.category_type));
  const qs = search.toString();
  const data = await api<ProductRow[] | { results: ProductRow[] }>(
    `/api/admin/products/${qs ? `?${qs}` : ''}`
  );
  return asList(data);
}

export async function createProduct(value: ProductFormValue) {
  const row = await api<ProductRow>('/api/admin/products/', {
    method: 'POST',
    body: JSON.stringify(productToPayload(value)),
  });
  void invalidateMarketplaceCatalog();
  return row;
}

export async function updateProduct(id: number, value: ProductFormValue) {
  const row = await api<ProductRow>(`/api/admin/products/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(productToPayload(value)),
  });
  void invalidateMarketplaceCatalog();
  return row;
}

export async function fetchBlobs(q?: string) {
  const search = new URLSearchParams();
  if (q) search.set('q', q);
  const qs = search.toString();
  return api<BlobListResponse>(`/api/admin/marketplace/blobs/${qs ? `?${qs}` : ''}`);
}

export async function uploadBlob(file: File, name?: string, overwrite = false) {
  const form = new FormData();
  form.append('file', file);
  if (name) form.append('name', name);
  form.append('overwrite', overwrite ? 'true' : 'false');
  const res = await fetch('/api/admin/marketplace/blobs/', {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = await res.json();
      message = body?.detail || body?.error || message;
    } catch {
      /* ignore */
    }
    throw new Error(message || 'Upload failed');
  }
  return res.json() as Promise<{ name: string; url: string; content_type?: string }>;
}
